import { useState, useEffect } from 'react'
import { Save, Settings, Euro, Clock, Mail, Truck, Users, BarChart3, Calendar } from 'lucide-react'
import api from '@/lib/api'
import { useToast } from '@/components/ui/Toast'

interface Config {
  id: number
  pvp_shipping_cost: number
  max_return_days: number
  notification_email: string
  default_carrier: string
  is_active: boolean
  pvd_min_orders: number
  pvd_max_return_rate: number
  pvd_evaluation_months: number
  pvd_shipping_cost: number
  updated_at: string | null
}

const CARRIERS = [
  { code: 'mrw', name: 'MRW' },
  { code: 'gls', name: 'GLS' },
  { code: 'ctt', name: 'CTT Express' }
]

export default function ReturnsConfigTab() {
  const [config, setConfig] = useState<Config | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  // Form state
  const [pvpShippingCost, setPvpShippingCost] = useState(8.90)
  const [maxReturnDays, setMaxReturnDays] = useState(14)
  const [notificationEmail, setNotificationEmail] = useState('')
  const [defaultCarrier, setDefaultCarrier] = useState('mrw')
  const [isActive, setIsActive] = useState(true)
  const [pvdMinOrders, setPvdMinOrders] = useState(3)
  const [pvdMaxReturnRate, setPvdMaxReturnRate] = useState(20)
  const [pvdEvaluationMonths, setPvdEvaluationMonths] = useState(24)
  const [pvdShippingCost, setPvdShippingCost] = useState(8.90)

  useEffect(() => {
    loadConfig()
  }, [])

  const loadConfig = async () => {
    setLoading(true)
    try {
      const response = await api.get('/returns/admin/config')
      const data = response.data
      setConfig(data)
      setPvpShippingCost(data.pvp_shipping_cost)
      setMaxReturnDays(data.max_return_days)
      setNotificationEmail(data.notification_email)
      setDefaultCarrier(data.default_carrier)
      setIsActive(data.is_active)
      setPvdMinOrders(data.pvd_min_orders ?? 3)
      setPvdMaxReturnRate(data.pvd_max_return_rate ?? 20)
      setPvdEvaluationMonths(data.pvd_evaluation_months ?? 24)
      setPvdShippingCost(data.pvd_shipping_cost ?? 8.90)
    } catch (error) {
      console.error('Error loading config:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.put('/returns/admin/config', {
        pvp_shipping_cost: pvpShippingCost,
        max_return_days: maxReturnDays,
        notification_email: notificationEmail,
        default_carrier: defaultCarrier,
        is_active: isActive,
        pvd_min_orders: pvdMinOrders,
        pvd_max_return_rate: pvdMaxReturnRate,
        pvd_evaluation_months: pvdEvaluationMonths,
        pvd_shipping_cost: pvdShippingCost
      })

      toast.success('Exito', 'Configuracion guardada correctamente')

      loadConfig()
    } catch (error: any) {
      toast.error('Error', error.message || 'Error al guardar configuracion')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-2xl">
      <div className="mb-6">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Configuracion del modulo de devoluciones
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Ajusta los parametros del sistema de devoluciones
        </p>
      </div>

      <div className="space-y-6">
        {/* Estado del modulo */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-slate-900 dark:text-white">Estado del modulo</h3>
              <p className="text-sm text-slate-500 mt-1">
                Activa o desactiva el sistema de devoluciones
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-indigo-600"></div>
            </label>
          </div>
        </div>

        {/* Coste de envio PVP */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
              <Euro className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-medium text-slate-900 dark:text-white">Coste de envio para clientes PVP</h3>
              <p className="text-sm text-slate-500 mt-1">
                Importe que se descontara del reembolso para clientes de tipo PVP.
                Los clientes PVD y VIP tienen recogida gratuita.
              </p>
            </div>
          </div>
          <div className="relative w-48">
            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="number"
              step="0.01"
              min="0"
              value={pvpShippingCost}
              onChange={(e) => setPvpShippingCost(parseFloat(e.target.value) || 0)}
              className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            />
          </div>
        </div>

        {/* Configuracion PVD (distribuidores) */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-medium text-slate-900 dark:text-white">Coste de envio para distribuidores (PVD)</h3>
              <p className="text-sm text-slate-500 mt-1">
                Los distribuidores tienen recogida gratuita por defecto. Sin embargo, si un distribuidor supera
                un umbral de tasa de devoluciones, se le cobra el coste de recogida. Esto penaliza a los distribuidores
                que devuelven un porcentaje excesivo de sus compras.
              </p>
            </div>
          </div>

          <div className="space-y-5 ml-12">
            {/* Pedidos minimos */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Pedidos minimos para evaluar
              </label>
              <p className="text-xs text-slate-400 mb-2">
                Si el distribuidor tiene menos de este numero de pedidos en el periodo de evaluacion,
                la devolucion es siempre gratuita. Esto protege a clientes nuevos o con poca actividad.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={pvdMinOrders}
                  onChange={(e) => setPvdMinOrders(parseInt(e.target.value) || 3)}
                  className="w-24 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                />
                <span className="text-sm text-slate-500">pedidos</span>
              </div>
            </div>

            {/* Tasa maxima */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Tasa maxima de devoluciones
              </label>
              <p className="text-xs text-slate-400 mb-2">
                Porcentaje maximo de facturas rectificativas sobre facturas normales. Si el distribuidor
                supera este umbral, pagara el coste de recogida. Por ejemplo, con un 20%: un distribuidor
                con 10 pedidos y 2 o mas devoluciones pagaria.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={pvdMaxReturnRate}
                  onChange={(e) => setPvdMaxReturnRate(parseInt(e.target.value) || 20)}
                  className="w-24 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                />
                <span className="text-sm text-slate-500">%</span>
              </div>
            </div>

            {/* Periodo evaluacion */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Periodo de evaluacion
              </label>
              <p className="text-xs text-slate-400 mb-2">
                Cuantos meses hacia atras se analizan las facturas del distribuidor en Factusol
                para calcular su tasa de devoluciones. Un periodo mas largo da una vision mas estable.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={pvdEvaluationMonths}
                  onChange={(e) => setPvdEvaluationMonths(parseInt(e.target.value) || 24)}
                  className="w-24 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                />
                <span className="text-sm text-slate-500">meses</span>
              </div>
            </div>

            {/* Coste PVD */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Coste de recogida para PVD penalizado
              </label>
              <p className="text-xs text-slate-400 mb-2">
                Importe que se cobra al distribuidor que supera la tasa maxima de devoluciones.
                Se descuenta del reembolso, igual que con los clientes PVP.
              </p>
              <div className="relative w-48">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={pvdShippingCost}
                  onChange={(e) => setPvdShippingCost(parseFloat(e.target.value) || 0)}
                  className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                           bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                />
              </div>
            </div>

            {/* Resumen visual */}
            <div className="bg-purple-50 dark:bg-purple-900/10 rounded-lg p-4 border border-purple-100 dark:border-purple-800/30">
              <p className="text-sm text-purple-800 dark:text-purple-300 font-medium mb-2">Resumen de la regla actual:</p>
              <ul className="text-sm text-purple-700 dark:text-purple-400 space-y-1">
                <li>Distribuidor con menos de <strong>{pvdMinOrders}</strong> pedidos en {pvdEvaluationMonths} meses → <strong>gratis</strong></li>
                <li>Distribuidor con tasa de devoluciones menor al <strong>{pvdMaxReturnRate}%</strong> → <strong>gratis</strong></li>
                <li>Distribuidor con tasa igual o superior al <strong>{pvdMaxReturnRate}%</strong> → <strong>{pvdShippingCost.toFixed(2)}€</strong></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Plazo de devolucion */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-medium text-slate-900 dark:text-white">Plazo maximo de devolucion</h3>
              <p className="text-sm text-slate-500 mt-1">
                Numero de dias desde la entrega del pedido durante los cuales el cliente puede solicitar una devolucion.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-48">
            <input
              type="number"
              min="1"
              max="365"
              value={maxReturnDays}
              onChange={(e) => setMaxReturnDays(parseInt(e.target.value) || 14)}
              className="w-24 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
            />
            <span className="text-slate-500">dias</span>
          </div>
        </div>

        {/* Email de notificaciones */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Mail className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-medium text-slate-900 dark:text-white">Email de notificaciones internas</h3>
              <p className="text-sm text-slate-500 mt-1">
                Direccion de email que recibira notificaciones sobre nuevas solicitudes de devolucion.
              </p>
            </div>
          </div>
          <input
            type="email"
            value={notificationEmail}
            onChange={(e) => setNotificationEmail(e.target.value)}
            placeholder="info@jvs-informatica.com"
            className="w-full max-w-sm px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                     bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          />
        </div>

        {/* Transportista por defecto */}
        <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
              <Truck className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <h3 className="font-medium text-slate-900 dark:text-white">Transportista por defecto</h3>
              <p className="text-sm text-slate-500 mt-1">
                Transportista que se usara para generar las etiquetas de recogida.
              </p>
            </div>
          </div>
          <select
            value={defaultCarrier}
            onChange={(e) => setDefaultCarrier(e.target.value)}
            className="w-48 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                     bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          >
            {CARRIERS.map(c => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Boton guardar */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Guardar cambios
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
